import { cva } from "class-variance-authority";
import type { ReactNode } from "react";
import { Text } from "../../typography/Text";
import { classNames } from "../../utils/classNames";
import { FormError } from "../FormError";
import type { BaseFormProps } from "../types";
import { FormLabel } from "./FormLabel";

export const formControlStyles = cva("block w-full text-neutral text-sm", {
	variants: {
		labelPosition: {
			top: "",
			left: "flex flex-row flex-wrap items-center justify-between gap-2",
			right: "flex flex-row-reverse flex-wrap items-center justify-end gap-2",
		},
	},
	defaultVariants: {
		labelPosition: "top",
	},
});

export const formErrorStyles = cva("mt-0.5", {
	variants: {
		labelPosition: {
			top: "",
			left: "mx-0 w-full",
			right: "mx-0 w-full",
		},
	},
	defaultVariants: {
		labelPosition: "top",
	},
});

export type FormControlProps = BaseFormProps & {
	children: ReactNode;
	hideError?: boolean;
	labelClassName?: string;
	errorClassName?: string;
};

export const FormControl = ({
	name,
	error,
	label,
	children,
	isRequired,
	labelPosition,
	description,
	containerClassName,
	formHelperText,
	hideError,
	labelClassName,
	errorClassName,
}: FormControlProps) => {
	return (
		<div className={classNames(formControlStyles({ labelPosition }), containerClassName)}>
			{label && (
				<div>
					<FormLabel htmlFor={name} isRequired={isRequired} className={labelClassName}>
						{label}
					</FormLabel>
					{description && (
						<Text intent="muted" size="sm" className="mt-1 max-w-prose leading-5">
							{description}
						</Text>
					)}
				</div>
			)}
			{children}
			{formHelperText && (
				<Text size="sm" intent="muted" className="mt-0.5">
					{formHelperText}
				</Text>
			)}
			{error && !hideError && (
				<FormError
					className={classNames(formErrorStyles({ labelPosition }), errorClassName)}
					id={`${name.replace(/\./g, "-")}-error`}
					error={error}
				/>
			)}
		</div>
	);
};
